import React from 'react';
import styled, { css, injectGlobal } from 'styled-components';

import { SemanticUiOverride } from './SemanticUiOverride';

export const PUSHER_AND_SIDE_MS_TRANSITION = 250;

export const Sizes = {
	IPHONE_SE: 320,
	PHONE: 376,
	PHABLET: 572,
	TABLET: 768,
	DESKTOP: 992,
	LARGE: 1200,
	EXTRA_LARGE: 1440,
	WIDE_SCREEN: 1920,

	// Material Design brakpoints: https://material-ui.com/layout/breakpoints/
	// `xs` -> anything below `MUI_SM`
	MUI_SM: 600,
	MUI_MD: 960,
	MUI_LG: 1280,
	MUI_LM: 1440, // Custom breakpoint
	MUI_XL: 1920,
};

/**
 * DEPRECATION WARNING: we are moving away from using the colors below
 * in favor of the color guidelines in Stellar:
 * http://stellar.devposlavu.com/styles/colors (requires VPN)
 * See also pinned message on #lavu4cp in Slack.
 */
export const Colors = {
	DARK_BLUE: '#18115b',
	DARK_PURPLE: '#4a2151',
	WHITE: '#FFF',
	DARK_WHITE: '#FAFAFA',
	BLACK: '#000',
	LIGHT_BLACK: '#6A6A6A',
	LIGHT_BLACK_1: '#23163F',
	DARK_BLACK: '#444444',
	DARKER_BLACK: '#373737',
	ALT_BLACK: '#2C2C2C',
	LIGHT_GREY: '#EBEBEB',
	LIGHT_GREY_2: '#E3E3E3',
	LIGHT_GREY_3: '#F8F8F8',
	GREY: '#BDBDBD',
	DARK_GREY: '#8B8B8B',
	DARK_GREY_2: '#999',
	DARK_GREY_3: '#efefef',
	BORDER_SUCCESS: '#89a709',
	TOAST_SUCCESS: '#AECD37',
	TOAST_SUCCESS_BORDER: '#88A807',
	DEFAULT: '#F4F4F2',
	PRIMARY: '#1A6989',
	SUCCESS: '#99BC07',
	WARNING: '#C9AA2B',
	DANGER_2: '#e8301b',
	WARNING_2: '#f68e20',
	DANGER: '#E93259',
	PURPLE: '#23163F',
	BLUE: '#3CA5D9',
	PRIMARY_TEXT: '#23163f',
};

export const HoverColors = {
	DEFAULT: '#EBEBEB',
	PRIMARY: '#3382A9',
	SUCCESS: '#AECD37',
	WARNING: '#EACF4A',
	DANGER: '#AD0000',
	HOVER_GREEN: '#88A807',
};

export const media = (type, arg = null) =>
	Object.keys(Sizes).reduce((accumulator, label) => {
		accumulator[label] = (...args) => css`
      @media (${type}: ${arg || Sizes[label]}px) {
        ${css(...args)};
      }
    `;

		return accumulator;
	}, {});

export const modifiedMinWidth = arg => media('min-width', arg);

export const minMedia = media('min-width');

export const modifiedMaxWidth = arg => media('max-width', arg);

export const maxMedia = media('max-width');

export const BaseStyles = () => injectGlobal`
	${SemanticUiOverride()}
	body {
		color: ${Colors.PRIMARY_TEXT}
	}
  .main-section {
    height: calc(100vh - ${getTopMenuHeight()}px);
    overflow: auto;
	-webkit-overflow-scrolling: touch;

    > iframe {
      border: 0;
      margin: 0;
      padding: 0;
    }
  }
`;

const getTopMenuHeight = () => {
	const menuTop = document.querySelector('.menu--top');
	const menuBottom = document.querySelector('.menu--bottom');
	let menuHeight = 50;

	if (menuTop) {
		menuHeight += Math.ceil(menuTop.getBoundingClientRect().height || 0);
	}

	if (menuBottom) {
		menuHeight += Math.ceil(menuBottom.getBoundingClientRect().height || 0);
	}

	return menuHeight;
};

export const SelectTitle = styled.div`
	margin: 0 0 0.5rem 0;
	color: ${Colors.PRIMARY_TEXT};
	font-weight: bold;
	font-size: 17px;
	font-weight: 600;
`;

export const Spacer = styled.div`
  ${({ width }) => (width ? `width: ${width}px;` : 'width: 100%;')}
  display: ${({ width }) => (width ? 'inline-block' : 'block')};
  ${({ height }) => (height ? `height: ${height}px;` : 'height: 100%;')}
`;

export const FixedContentHeader = styled.div`
	background: white;
	padding: 15px;
	min-height: 50px;
	z-index: 2;
	box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.09);

	${({ visible = true }) => !visible && 'display: none'};
`;

export const StyledLink = styled(({ Component, children, ...rest }) =>
	Component ? (
		<Component {...rest}>{children}</Component>
	) : (
		<a {...rest}>{children}</a>
	)
)`
	text-decoration: ${props => props.textDecoration || 'none'};
	color: ${props => props.color || '#5a77d8'};
`;

/**
 * Handly utility to simulate adding opacity to a color in hex notation.
 * Basically transforms the hex color into rgb and adds the opacity in
 * rgba notation. We could make this easier in the future using rrggbbaa notation
 * but currently is not supported by IE nor Edge
 *
 * Check: https://caniuse.com/#search=hex
 *
 * @param {String} color Hex color like #ccc or #45fc7a
 * @param {Number} opacity Float between 0 and 1
 *
 * @returns {String} Color in rgba notation
 */
export const addColorOpacity = (color, opacity) => {
	let [...hexColor] = color.slice(1); // discard firs character '#'

	if (hexColor.length !== 3 && hexColor.length !== 6) {
		throw new Error('Invalid hex color');
	}

	if (hexColor.length === 3) {
		const [r, g, b] = hexColor;
		hexColor = [r, r, g, g, b, b];
	}

	const hexComponents = [
		hexColor.slice(0, 2),
		hexColor.slice(2, 4),
		hexColor.slice(4, 6),
	];
	const rgb = hexComponents.map(c => parseInt(c.join(''), 16)).join();

	return `rgba(${rgb},${opacity})`;
};

/**
 * Takes an RGB or RGBA color string and returns a new RGBA string
 * with the provided `opacityLevel`.
 *
 * RGBA color with existing opacity: `rgba(32, 24, 61, 0.5);`
 * RGBA color without existing opacity: `rgba(32, 24, 61);`
 * RGB color: `rgb(32, 24, 61);`
 *
 * Note: passing an ending semicolon is not required.
 *
 * @param {string} color - RGBA string
 * @param {number|string} opacityLevel
 * @returns {string} - a new RGBA string
 */
export const modColorOpacity = (color, opacityLevel = 1) => {
	if (!color || typeof color !== 'string' || !color.includes('rgb')) {
		throw new Error(
			'Invalid "color" argument provided to function. Make sure you are passing a valid rgba color string.'
		);
	}

	// Remove any semicolon or closing parenthesis
	const nextColorStr = color.replace(/[);]/g, '');
	const colorArr = nextColorStr.split(' ');

	// The color string has no opacity
	if (colorArr.length === 3) {
		colorArr.push(`,${opacityLevel})`);

		return colorArr.join('');
	} else if (colorArr.length === 4) {
		colorArr[3] = `${opacityLevel})`;

		return colorArr.join('');
	}

	throw new Error('One or more invalid arguments provided to function.');
};
